// CourseView — extracted from App.js (Feb 2026 refactor).
// Behavior-preserving 1:1 copy. Rendered inside a Layout received as prop.

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEscClose } from "../hooks/useEscClose";
import CourseUeBreakdown from "../components/CourseUeBreakdown";
import { PruefungsunterlagenCard, PraktikumsunterlagenCard } from "../components/PruefungsunterlagenCard";
import { TagesmaterialCard } from "../components/TagesmaterialCard";
import { UebungsquizCard } from "../components/UebungsquizCard";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle, ArrowLeft, ArrowRight, Award, BookOpen, Check,
  CheckCircle2, Clock, Download, Loader2, Lock, PenTool,
  PlayCircle, Target, X,
} from "lucide-react";

import { Button, Card, ProgressRing } from "../components/UIPrimitives";
import ProgressGapsView from "../components/ProgressGapsView";
import ScriptDownloadCTA from "../components/ScriptDownloadCTA";
import ModullisteCard from "../components/ModullisteCard";
import AnwesenheitCard from "../components/AnwesenheitCard";
import { MiniPoster } from "../components/GalleryWall";
import FacharbeitUploadCard from "../components/FacharbeitUploadCard";
import ExamCard from "../components/ExamCard";
import { useAuth } from "../contexts/AuthContext";
import { getCourseCover, getCourseIntro } from "../lib/courseCovers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// AZAV-Maßnahmen mit trägerinterner Abschlussprüfung (Dickies-Campus GmbH)
const AZAV_BG_COURSES = ['course-behandlungspflege-lg', 'course-ambulanter-pflegeassistent', 'course-pflegehilfskraft'];

const SCHULUNGSPLAN_FILES = {
  'course-behandlungspflege-lg': { file: '/docs/Schulungsplan_Behandlungspflege_LG1_LG2.pdf', tage: 32 },
  'course-ambulanter-pflegeassistent': { file: '/docs/Schulungsplan_Ambulanter_Pflegeassistent.pdf', tage: 46 },
  'course-pflegehilfskraft': { file: '/docs/Schulungsplan_Pflegehilfskraft_mit_Betreuung.pdf', tage: 66 },
};

const CourseView = ({ Layout }) => {
  const { courseId } = useParams();
  const { getAuthHeader, user } = useAuth();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [courseStatus, setCourseStatus] = useState(null);
  const [assignVariant, setAssignVariant] = useState(null);
  const [meRole, setMeRole] = useState(null);
  const isAzavKurs = AZAV_BG_COURSES.includes(courseId);
  // BG ist Standard in den AZAV-Kursen — nur explizite Selbstlerner (und Admins) sehen die volle Ansicht
  const isBG = isAzavKurs && meRole !== 'admin' && assignVariant === 'bg';
  const [loading, setLoading] = useState(true);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const navigate = useNavigate();
  
  // Signature state for participant certificate request
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [submittingSignature, setSubmittingSignature] = useState(false);
  const [certificateStatus, setCertificateStatus] = useState(null);
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // "Was fehlt"-Modal — shown when user clicks a locked module
  const [gapsModal, setGapsModal] = useState(null); // { lockedModuleTitle, lockedModuleOrder, gaps: [] }
  useEscClose(() => { setShowSignatureModal(false); setGapsModal(null); });
  useEffect(() => {
    if (course?.title) document.title = `${course.title} · Dickies-Campus`;
  }, [course]);
  const [gapsLoading, setGapsLoading] = useState(false);

  const openGapsModal = async (lockedModule) => {
    setGapsLoading(true);
    setGapsModal({ lockedModuleTitle: lockedModule.title, lockedModuleOrder: lockedModule.order, gaps: [] });
    try {
      const res = await axios.get(`${API}/courses/${courseId}/progress-gaps`, getAuthHeader());
      // Only show gaps from modules BEFORE the clicked one
      const relevantGaps = (res.data.gaps || []).filter(
        g => g.module_order < (lockedModule.order || 999)
      );
      setGapsModal({
        lockedModuleTitle: lockedModule.title,
        lockedModuleOrder: lockedModule.order,
        gaps: relevantGaps,
      });
    } catch (e) {
      setGapsModal({ lockedModuleTitle: lockedModule.title, lockedModuleOrder: lockedModule.order, gaps: [], error: true });
    }
    setGapsLoading(false);
  };

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const [courseRes, modulesRes, progressRes, statusRes, certStatusRes, variantRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, getAuthHeader()),
        axios.get(`${API}/courses/${courseId}/modules`, getAuthHeader()),
        axios.get(`${API}/user/progress/${courseId}`, getAuthHeader()),
        axios.get(`${API}/user/course-status/${courseId}`, getAuthHeader()).catch(() => ({ data: null })),
        axios.get(`${API}/user/certificate-status/${courseId}`, getAuthHeader()).catch(() => ({ data: null })),
        axios.get(`${API}/user/assignment-variant/${courseId}`, getAuthHeader()).catch(() => ({ data: { variant: null } }))
      ]);
      setCourse(courseRes.data);
      setModules(modulesRes.data);
      setProgress(progressRes.data);
      setCourseStatus(statusRes.data);
      setCertificateStatus(certStatusRes.data);
      setAssignVariant(variantRes.data?.variant || null);
      setMeRole(variantRes.data?.role || null);
      try {
        const done = (progressRes.data || []).filter(p => p.completed || p.quiz_passed).filter(p => !p.fallbeispiele_required || p.fallbeispiele_done).length;
        const v = variantRes.data?.variant || null;
        const bgSide = AZAV_BG_COURSES.includes(courseRes.data.id) && variantRes.data?.role !== 'admin' && v === 'bg';
        localStorage.setItem('dh_current_reading', JSON.stringify({
          id: courseRes.data.id, title: courseRes.data.title, done, total: (modulesRes.data || []).length,
          bg: bgSide, tage: SCHULUNGSPLAN_FILES[courseRes.data.id]?.tage || null,
        }));
        window.dispatchEvent(new CustomEvent('dh-reading-updated'));
      } catch { /* ignore */ }
    } catch (error) {
      toast.error("Fehler beim Laden des Kurses");
    }
    setLoading(false);
  };

  // Signature functions for participant
  const initSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  };

  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature && signatureCanvasRef.current) {
      setSignatureData(signatureCanvasRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureData('');
  };

  // Submit participant signature for certificate request
  const submitSignature = async () => {
    if (!signatureData) {
      toast.error("Bitte unterschreiben Sie zuerst");
      return;
    }
    
    setSubmittingSignature(true);
    try {
      await axios.post(
        `${API}/user/certificate-signature/${courseId}`,
        { signature_data: signatureData },
        getAuthHeader()
      );
      toast.success("Unterschrift eingereicht! Der Dozent wird Ihre Teilnahmebescheinigung fertigstellen.");
      setShowSignatureModal(false);
      // Reload to update certificate status
      loadCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Einreichen");
    }
    setSubmittingSignature(false);
  };

  const downloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const response = await axios.get(`${API}/user/certificate/${courseId}`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Zertifikat_${course?.title?.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Zertifikat heruntergeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Download");
    }
    setDownloadingCert(false);
  };

  const downloadAttendance = async () => {
    setDownloadingCert(true);
    try {
      const response = await axios.get(`${API}/user/attendance-confirmation/${courseId}`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Teilnahmebescheinigung_${course?.title?.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Teilnahmebescheinigung heruntergeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Download");
    }
    setDownloadingCert(false);
  };

  const [exportingTasks, setExportingTasks] = useState(false);
  const [downloadingPraktikum, setDownloadingPraktikum] = useState(false);

  const PRAKTIKUM_CONFIG = {
    'course-intensivpflege-akip': {
      endpoint: 'intensivpflege/praktikumsnachweis',
      filename: 'Praktikumsnachweis_Ausserklinische_Intensivpflege.pdf',
      kicker: 'Praktikums-Vorlage',
      title: 'Praktikumsnachweis (60 h) und Kompetenzcheck',
      desc: '— offizielle Vorlage nach § 132l SGB V. Ausdrucken, ausfüllen, unterschreiben lassen.',
    },
    'course-pflegeberater': {
      endpoint: 'pflegeberater/praktikumsnachweis',
      filename: 'Praktikumsnachweis_Pflegeberater_7a.pdf',
      kicker: 'Praktikums-Vorlage',
      title: 'Praktikumsnachweis (9 Tage) und Kompetenzcheck',
      desc: '— Vorlage nach den GKV-Empfehlungen zu §7a SGB XI. Ausdrucken, im selbst organisierten Praktikum ausfüllen, unterschreiben lassen.',
    },
    'course-wundexperte-84': {
      endpoint: 'wundexperte/hospitationsnachweis',
      filename: 'Hospitationsnachweis_Wundexperte.pdf',
      kicker: 'Hospitations-Vorlage',
      title: 'Hospitationsnachweis (16 Std.) und Kompetenzcheck',
      desc: '— für die selbst organisierte Hospitation, z. B. in Wundambulanz, Wundzentrum oder Praxis mit Wundschwerpunkt. Ausdrucken, ausfüllen, unterschreiben lassen.',
    },
    'course-weiterbildung-wundexperte': {
      endpoint: 'wundtherapeut/hospitationsnachweis',
      filename: 'Hospitationsnachweis_Wundtherapeut.pdf',
      kicker: 'Hospitations-Vorlage',
      title: 'Hospitationsnachweis (40 Std.) und Kompetenzcheck',
      desc: '— für die selbst organisierte Hospitation, z. B. in Wundambulanz, Wundzentrum oder Praxis mit Wundschwerpunkt. Ausdrucken, ausfüllen, unterschreiben lassen.',
    },
    'course-intensivpflege-basis': {
      endpoint: 'intensivbasis/hospitationsnachweis',
      filename: 'Hospitationsnachweis_Intensivpflege_Basiskurs.pdf',
      kicker: 'Hospitations-Vorlage',
      title: 'Hospitationsnachweis (40 Std.) und Kompetenzcheck',
      desc: '— für die selbst organisierte Hospitation, z. B. auf Intensivstation, im Weaning-Zentrum oder in einer Beatmungs-WG. Ausdrucken, ausfüllen, unterschreiben lassen.',
    },
    'course-externenpruefung-pfa': {
      endpoint: 'externenpruefung/checkliste',
      filename: 'Checkliste_Externenpruefung_Pflegefachassistenz.pdf',
      kicker: 'Checkliste',
      title: 'Checkliste Externenprüfung — alle Nachweise im Überblick',
      desc: '— welche Nachweise für die Anmeldung zur Externenprüfung Pflegefachassistenz erforderlich sind. Ausdrucken, Punkt für Punkt abhaken und Unterlagen zusammenstellen.',
    },
  };
  const praktikumConfig = PRAKTIKUM_CONFIG[courseId];

  const downloadPraktikumsnachweis = async () => {
    if (!praktikumConfig) return;
    setDownloadingPraktikum(true);
    try {
      const response = await axios.get(`${API}/user/${praktikumConfig.endpoint}`, {
        ...getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', praktikumConfig.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Praktikumsnachweis heruntergeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Download');
    }
    setDownloadingPraktikum(false);
  };

  const downloadOwnTaskAnswers = async () => {
    setExportingTasks(true);
    try {
      const response = await axios.get(`${API}/user/my-tasks/${courseId}/export`, {
        ...getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Meine_Aufgaben_${course?.title?.replace(/\s+/g, '_') || 'Kurs'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Antworten exportiert!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Export");
    }
    setExportingTasks(false);
  };

  const [exportingPdf, setExportingPdf] = useState(false);

  const downloadOwnTasksPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/user/my-tasks/${courseId}/export-pdf`, {
        ...getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Mein_Nachweis_${course?.title?.replace(/\s+/g, '_') || 'Kurs'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF-Nachweis heruntergeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim PDF-Export");
    }
    setExportingPdf(false);
  };

  const getModuleProgress = (moduleId) => {
    return progress.find(p => p.module_id === moduleId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra-dark" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Background atmosphere blobs */}
      <div className="fixed -top-20 -right-32 w-96 h-96 rounded-full bg-terra/[0.05] blur-3xl pointer-events-none -z-10" aria-hidden />
      <div className="fixed top-1/3 -left-32 w-80 h-80 rounded-full bg-cream-200/60 blur-3xl pointer-events-none -z-10" aria-hidden />

      <div className="max-w-4xl mx-auto animate-fade-in relative">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sand-700 hover:text-terra-dark mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Übersicht
        </button>

        {/* Hero Card */}
        {(() => {
          const totalModules = modules.length;
          const completedCount = progress.filter(p => p.completed || p.quiz_passed).filter(p => !p.fallbeispiele_required || p.fallbeispiele_done).length;
          const progressPct = totalModules > 0 ? (completedCount / totalModules) * 100 : 0;
          // First non-completed module = "Hier weitermachen"
          const nextModuleIdx = modules.findIndex(m => {
            const mp = progress.find(p => p.module_id === m.id);
            return !(mp?.completed || mp?.quiz_passed) || (mp?.fallbeispiele_required && !mp?.fallbeispiele_done);
          });
          const nextModule = nextModuleIdx >= 0 ? modules[nextModuleIdx] : null;
          const ctaOrder = nextModule ? (nextModule.order || (nextModuleIdx + 1)) : null;
          const totalUE = course?.total_ue || modules.reduce((sum, m) => sum + (m.ue || m.ue_count || 0), 0);
          // Strip leading "Modul X:" from next-module title
          const stripPrefix = (t) => (t || '').replace(/^modul\s*\d+\s*[:\-–—]\s*/i, '').trim();

          const titleWords = (course?.title || '').split(' ');
          const lastWord = titleWords.pop() || '';
          const titleHead = titleWords.join(' ');

          return (
            <>
            {/* HERO — Aufmacher einer Ausgabe */}
            <div className="relative overflow-hidden rounded-3xl mb-8 h-[300px] md:h-[380px]" data-testid="course-hero">
              <img
                src={getCourseCover(course?.id || courseId)}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(to top, rgba(24,21,18,0.88) 0%, rgba(24,21,18,0.35) 45%, transparent 65%)" }}
              />
              <div className="absolute bottom-7 left-7 md:bottom-9 md:left-9 right-7">
                <p className="text-[11px] uppercase font-semibold mb-3" style={{ letterSpacing: "0.3em", color: "#D89232", textShadow: "0 1px 8px rgba(24,21,18,0.6)" }}>
                  Ausgabe MMXXVI · {totalUE} UE · {course?.requires_exam ? (isAzavKurs ? "Mit trägerinterner Abschlussprüfung" : "Mit externer Prüfung") : (course?.has_certificate ? "Zertifikatskurs" : "Weiterbildung")}{course?.massnahmenummer ? ` · AZAV-Maßnahme ${course.massnahmenummer}` : ""}
                </p>
                <h1 className="font-display text-[28px] md:text-[44px]" style={{ color: "#F5F0EA", lineHeight: 1.15 }} data-testid="course-title">
                  {titleHead}{titleHead && " "}<span className={/[§\d]/.test(lastWord) ? "" : "italic"} style={{ color: "#E7CFA2" }}>{lastWord}</span>.
                </h1>
                <p className="font-display italic mt-2.5 text-[15px] md:text-base" style={{ color: "rgba(245,240,234,0.85)" }}>
                  {getCourseIntro(course?.id || courseId, course?.description)}
                </p>
              </div>
            </div>

            {/* STECKBRIEF — Magazin-Impressum */}
            <div className="grid grid-cols-3 mb-10" data-testid="course-impressum">
              {[
                isBG && SCHULUNGSPLAN_FILES[courseId]
                  ? { label: "Unterrichtstage", value: SCHULUNGSPLAN_FILES[courseId].tage }
                  : { label: "Kapitel", value: totalModules },
                { label: "Umfang", value: `${totalUE} UE` },
                { label: "Abschluss", value: course?.requires_exam ? (isAzavKurs ? "Trägerinterne Prüfung" : "Externe Prüfung") : (course?.has_certificate ? "Zertifikat" : "Bescheinigung") },
              ].map((col, ci) => (
                <div
                  key={col.label}
                  className="text-center px-4 py-2"
                  style={ci > 0 ? { borderLeft: "1px solid rgba(216,146,50,0.4)" } : undefined}
                >
                  <div className="font-mono text-[10px] uppercase font-semibold mb-1.5" style={{ letterSpacing: "0.25em", color: "#6B6157" }}>
                    {col.label}
                  </div>
                  <div className="font-display text-xl md:text-2xl text-espresso leading-none">{col.value}</div>
                </div>
              ))}
            </div>

            {/* OFFIZIELLE STUNDENAUFTEILUNG (nur Pflegeberater) */}
            <CourseUeBreakdown courseId={course?.id || courseId} />

            {/* LESEFORTSCHRITT */}
            {!isBG && (
            <div className="mb-10" data-testid="reading-progress">
              <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2.5">
                <span className="font-mono text-[11px] uppercase font-semibold" style={{ letterSpacing: "0.22em", color: "#6B6157" }}>
                  Dein Lesefortschritt · {completedCount} von {totalModules} Kapiteln
                </span>
                {progressPct >= 100 ? (
                  <span className="font-display italic text-terra text-lg leading-none">Ausgelesen.</span>
                ) : nextModule && (
                  <button
                    onClick={() => navigate(`/module/${nextModule.id}`)}
                    className="font-display italic text-terra hover:text-terra-dark text-[15px] inline-flex items-center gap-1.5 transition-colors"
                    data-testid="hero-cta-next-module"
                  >
                    Weiterlesen: Kapitel {ctaOrder} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="h-[2px] w-full rounded-full overflow-hidden" style={{ backgroundColor: "#E0D6CA" }}>
                <div
                  className="h-full"
                  style={{ width: `${progressPct}%`, backgroundColor: "#D89232", transition: "width 0.7s ease-out" }}
                />
              </div>
            </div>
            )}
            </>
          );
        })()}

        {/* Course Header context blocks (Certificate banner, exam hint) */}
        {!isBG && (
        <div className="mb-8">
          {course?.requires_exam && !course?.has_certificate && (
            <p className="font-display italic text-[15px] text-sand-600 mb-4">
              <span style={{ color: "#8F650F" }}>✳</span> {isAzavKurs
                ? 'Der Kurs schließt mit einer trägerinternen Abschlussprüfung (schriftlich & mündlich) — das Zertifikat wird nach bestandener Prüfung ausgestellt.'
                : 'Dieser Kurs erfordert eine externe Prüfung — das Zertifikat gibt es nach bestandener Prüfung.'}
            </p>
          )}
          
          {/* Certificate & Attendance Download Banner - show if certificate was issued */}
          {certificateStatus?.certificate_ready && (
            <div className="mt-4 p-2" style={{ backgroundColor: "#F5F0EA", border: "1px solid rgba(216,146,50,0.35)", borderRadius: 4 }}>
              <div className="px-6 py-6 text-center" style={{ border: "1px solid rgba(216,146,50,0.35)", borderRadius: 2 }}>
                <p className="font-mono text-[10px] uppercase font-semibold mb-2" style={{ letterSpacing: "0.25em", color: "#8F650F" }}>
                  Kurs abgeschlossen · {certificateStatus.certificate_issued_at ? new Date(certificateStatus.certificate_issued_at).toLocaleDateString('de-DE') : '-'}
                </p>
                <h3 className="font-display text-2xl text-espresso leading-snug mb-4">
                  Deine Nachweise sind <span className="italic text-terra">bereit</span>.
                </h3>
                <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-4">
                  <button
                    onClick={downloadAttendance}
                    disabled={downloadingCert}
                    className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
                    data-testid="download-attendance-banner"
                  >
                    {downloadingCert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />}
                    Teilnahmebescheinigung
                  </button>
                  <button
                    onClick={downloadCertificate}
                    disabled={downloadingCert}
                    className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
                    data-testid="download-certificate-banner"
                  >
                    {downloadingCert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />}
                    Zertifikat
                  </button>
                </div>
                <p className="font-display italic text-[13px] text-sand-600 leading-relaxed max-w-lg mx-auto">
                  Teilnahmebescheinigung: Inhaltsnachweis der vermittelten Themen · Zertifikat: offiziell mit Stempel &amp; QR-Verifizierung.
                </p>
              </div>
            </div>
          )}
          
          {/* Certificate Status for courses with certificate */}
          {course?.has_certificate && courseStatus && (
            <div className="mt-4 py-4 px-1 border-t border-b" style={{ borderColor: "rgba(216,146,50,0.35)" }}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {courseStatus.passed ? (
                      <>
                        <Award className="w-4 h-4" style={{ color: "#8F650F" }} />
                        <span className="font-display italic text-lg text-espresso">Kurs bestanden.</span>
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4 text-terra" />
                        <span className="font-semibold text-sand-800 text-sm">
                          Mindestens {courseStatus.passing_percentage}% zum Bestehen erforderlich
                        </span>
                      </>
                    )}
                  </div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-sand-600">
                    Aktueller Stand: {courseStatus.score_percentage}% · {courseStatus.correct_answers}/{courseStatus.total_questions} Fragen
                  </p>
                </div>

                {courseStatus.passed && (
                  <button
                    onClick={downloadCertificate}
                    disabled={downloadingCert}
                    className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
                    data-testid="download-certificate"
                  >
                    {downloadingCert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />}
                    Zertifikat herunterladen
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Participant Certificate Signature Section - only for courses requiring external exam */}
        {!isBG && course?.requires_exam && !certificateStatus?.certificate_ready && progress.filter(p => p.completed).length === modules.length && modules.length > 0 && (
          <div className="mb-8 p-2" style={{ backgroundColor: "#F5F0EA", border: "1px solid rgba(216,146,50,0.35)", borderRadius: 4 }} data-testid="congrats-card">
            <div className="px-7 py-8 md:px-10 text-center" style={{ border: "1px solid rgba(216,146,50,0.35)", borderRadius: 2 }}>
              <h3 className="font-display text-[26px] md:text-[30px] text-espresso leading-snug mb-3">
                Geschafft — diese Ausgabe ist <span className="italic text-terra">deine</span>.
              </h3>

              {certificateStatus?.participant_signature_submitted ? (
                <div className="space-y-3">
                  <p className="text-sm text-sand-700">
                    Deine Unterschrift wurde am {new Date(certificateStatus.submitted_at).toLocaleDateString('de-DE')} eingereicht.
                  </p>
                  {certificateStatus?.certificate_ready ? (
                    <button
                      onClick={downloadAttendance}
                      disabled={downloadingCert}
                      className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
                    >
                      {downloadingCert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Teilnahmebescheinigung herunterladen
                    </button>
                  ) : (
                    <p className="font-display italic text-[15px] text-sand-600">
                      Dein Dozent stellt die Teilnahmebescheinigung in Kürze fertig.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm text-sand-700 max-w-md mx-auto mb-6 leading-relaxed">
                    Du hast alle {modules.length} Kapitel gelesen. Mit deiner Unterschrift forderst du die Teilnahmebescheinigung an.
                  </p>
                  <button
                    onClick={() => {
                      setShowSignatureModal(true);
                      setTimeout(initSignatureCanvas, 100);
                    }}
                    className="dh-invite-btn mx-auto inline-flex items-center justify-center gap-2 h-[48px] px-10 rounded-full bg-transparent text-[13px] font-bold uppercase tracking-[0.2em]"
                    data-testid="request-certificate-btn"
                  >
                    <PenTool className="w-4 h-4" />
                    Unterschrift abgeben
                  </button>
                </>
              )}

              <p className="font-display italic text-[13px] text-sand-600 mt-5">
                <span style={{ color: "#8F650F" }}>✳</span> Das Zertifikat selbst gibt es nach deiner bestandenen {isAzavKurs ? 'trägerinternen Abschlussprüfung' : 'externen Prüfung'}.
              </p>
            </div>
          </div>
        )}

        {/* Signature Modal */}
        {showSignatureModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-cream-50 rounded-2xl border border-cream-300 p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-sand-800 flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-terra-dark" />
                  Teilnahmebescheinigung unterschreiben
                </h3>
                <button 
                  onClick={() => setShowSignatureModal(false)}
                  className="text-espresso/40 hover:text-espresso"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-sand-700 mb-4">
                Mit Ihrer Unterschrift bestätigen Sie die Teilnahme an der Weiterbildung 
                <strong className="text-terra-dark"> „{course?.title}“</strong> im Umfang von {course?.total_ue || modules.reduce((sum, m) => sum + (m.ue || 0), 0)} UE.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm text-sand-700 mb-2">
                  Ihre Unterschrift (mit Maus oder Finger zeichnen):
                </label>
                <div className="border-2 border-dashed border-cream-300 rounded-xl overflow-hidden bg-cream-100">
                  <canvas
                    ref={signatureCanvasRef}
                    width={400}
                    height={150}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                {hasSignature && (
                  <button
                    onClick={clearSignature}
                    className="text-sm text-terra hover:text-terra-dark mt-2"
                  >
                    Unterschrift löschen
                  </button>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowSignatureModal(false)}
                  variant="secondary"
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={submitSignature}
                  loading={submittingSignature}
                  disabled={!hasSignature}
                  className="flex-1"
                  data-testid="submit-signature-btn"
                >
                  Unterschrift einreichen
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Overall Progress card removed — replaced by reading-progress line in hero */}

        {/* Lehrbücher sind kostenlos: Skript-Download statt Shop-Verweis (alle Kurse) */}
        {!isBG && (
          <ScriptDownloadCTA courseId={courseId} courseTitle={course?.title} getAuthHeader={getAuthHeader} />
        )}

        {/* Externenprüfung PFA: Checkliste + Arbeitgeber-Bescheinigung */}
        {courseId === 'course-externenpruefung-pfa' && <PruefungsunterlagenCard />}

        {/* AZAV-Maßnahmen: Schulungsplan + Tagesmaterial — nur für BG-Teilnehmer (Selbstlerner haben den kompletten Stoff) */}
        {AZAV_BG_COURSES.includes(courseId) && (isBG || meRole === 'admin') && (
          <>
            {isBG && SCHULUNGSPLAN_FILES[courseId] && (
              <a
                href={SCHULUNGSPLAN_FILES[courseId].file}
                download
                data-testid="schulungsplan-download"
                className="flex items-center justify-between border border-sand-300 rounded-2xl px-6 py-4 mb-6 bg-cream-50 hover:border-terra transition-colors group"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra mb-1">AZAV-Maßnahme {course?.massnahmenummer}</p>
                  <p className="font-display text-[16px] text-espresso">Offizieller Schulungsplan — alle {SCHULUNGSPLAN_FILES[courseId].tage} Unterrichtstage (PDF)</p>
                </div>
                <span className="text-[11px] uppercase tracking-wider font-semibold px-4 py-2 rounded-full bg-espresso text-cream-50 group-hover:opacity-90 shrink-0">Herunterladen</span>
              </a>
            )}
            <TagesmaterialCard courseId={courseId} getAuthHeader={getAuthHeader} />
          </>
        )}

        {/* Course-specific: Facharbeit-Upload (Praxisanleiter / PDL) */}
        {['course-praxisanleiter', 'course-pdl-weiterbildung-teil1', 'course-pdl-weiterbildung-teil2', 'course-pdl-weiterbildung-teil3', 'course-gerontopsychiatrie', 'course-geronto-leitung'].includes(courseId) && (
          <div className="mb-8">
            <FacharbeitUploadCard courseId={courseId} getAuthHeader={getAuthHeader} />
          </div>
        )}

        {/* Galerie-Poster des Tages (AZAV-Maßnahmen: Dickies-Campus-Branding) */}
        <MiniPoster seed={courseId} className="mb-8" brand={isAzavKurs ? 'Dickies-Campus' : undefined} />

        {/* Präsenz-Anwesenheit (nur Kurse mit Präsenzpflicht) */}
        <AnwesenheitCard courseId={courseId} getAuthHeader={getAuthHeader} />

        {/* Modulliste mit Online-Unterschrift (nicht für BG-Maßnahme — keine Portal-Unterschriften) */}
        {!isBG && <ModullisteCard courseId={courseId} getAuthHeader={getAuthHeader} />}

        {/* Abschlussprüfung — sichtbar wenn der Admin eine Prüfung freigeschaltet hat */}
        <ExamCard courseId={courseId} getAuthHeader={getAuthHeader} />

        {/* Pflegehilfskraft & Betreuung: Praktikum (40 Zeitstunden, Pflichtbestandteil) mit CRM-Unterlagen */}
        {courseId === 'course-pflegehilfskraft' && <PraktikumsunterlagenCard />}

        {/* Course-specific: Praktikums-/Hospitationsnachweis */}
        {praktikumConfig && (
          <aside
            className="mb-8 rounded-xl bg-cream-100 border border-cream-300 hover:border-terra/30 transition-colors"
            data-testid="praktikumsnachweis-card"
          >
            <div className="flex items-center gap-4 sm:gap-5 px-5 sm:px-6 py-4">
              <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-terra/10 flex items-center justify-center">
                <PenTool className="w-5 h-5 text-terra" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-sand-500 mb-0.5">
                  {praktikumConfig.kicker}
                </div>
                <div className="text-sm sm:text-[14.5px] text-espresso/90 leading-snug">
                  <span className="font-semibold text-espresso">{praktikumConfig.title}</span>{' '}
                  {praktikumConfig.desc}
                </div>
              </div>
              <button
                onClick={downloadPraktikumsnachweis}
                disabled={downloadingPraktikum}
                data-testid="download-praktikumsnachweis-btn"
                className="group flex-shrink-0 inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
              >
                {downloadingPraktikum ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Lade…
                  </>
                ) : (
                  <>
                    Als PDF herunterladen
                    <Download className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />
                  </>
                )}
              </button>
            </div>
          </aside>
        )}

        {/* Modules — Inhaltsverzeichnis (BG-Maßnahme: freiwilliges Übungsquiz statt Pflicht-Lernstrecke) */}
        {isBG ? (
          <UebungsquizCard courseId={courseId} getAuthHeader={getAuthHeader} />
        ) : (
        <div>
          <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
            <div>
              <div className="flex items-center gap-2.5 mb-1.5">
                <span className="w-6 h-[2px]" style={{ backgroundColor: "#D89232" }} />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "#8F650F" }}>Inhaltsverzeichnis</span>
              </div>
              <h2 className="font-display text-3xl text-espresso tracking-tight leading-tight">Inhalt dieser <span className="italic text-terra">Ausgabe</span>.</h2>
            </div>
            <div className="flex items-center gap-x-6 gap-y-2 flex-wrap">
              {progress.some(p => p.tasks_submitted > 0 || p.completed || p.quiz_passed) && (
                <>
                <button
                  onClick={downloadOwnTaskAnswers}
                  disabled={exportingTasks}
                  className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
                  data-testid="download-my-task-answers"
                  title="Deine bisher eingereichten Aufgaben-Antworten als Excel-Datei herunterladen"
                >
                  <Download className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                  {exportingTasks ? 'Exportiere…' : 'Meine Antworten'}
                </button>
                <button
                  onClick={downloadOwnTasksPdf}
                  disabled={exportingPdf}
                  className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors disabled:opacity-50"
                  data-testid="download-my-tasks-pdf"
                  title="Deine Aufgaben-Dokumentation als schön formatiertes PDF — nach Modulen gegliedert"
                >
                  <Download className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                  {exportingPdf ? 'Erstelle PDF…' : 'Mein Nachweis (PDF)'}
                </button>
                </>
              )}
            </div>
          </div>

          <div className="border-t border-cream-300">
          {(() => {
            // Determine the "current" module = first incomplete module
            const currentIdx = modules.findIndex(m => {
              const mp = progress.find(p => p.module_id === m.id);
              return !(mp?.completed || mp?.quiz_passed) || (mp?.fallbeispiele_required && !mp?.fallbeispiele_done);
            });
            const stripPrefix = (t) => (t || '').replace(/^modul\s*\d+\s*[:\-–—]\s*/i, '').trim();

            return modules.map((module, index) => {
              const moduleProgress = getModuleProgress(module.id);
              const isCompleted = (moduleProgress?.completed || moduleProgress?.quiz_passed) && (!moduleProgress?.fallbeispiele_required || moduleProgress?.fallbeispiele_done);
              const isCurrent = !isCompleted && index === currentIdx;
              const cleanTitle = stripPrefix(module.title);
              const ue = module.ue_count || module.ue || 0;
              const quizScore = moduleProgress?.quiz_score ? Math.round(moduleProgress.quiz_score) : null;
              const nr = String(module.order || index + 1).padStart(2, '0');
              const readMin = Math.max(15, Math.round(ue * 2.5));
              const metaTime = `${ue} UE · ~${readMin} Min`;

              // STATE: Completed — gelesenes Kapitel
              if (isCompleted) {
                return (
                  <div
                    key={module.id}
                    onClick={() => navigate(`/module/${module.id}`)}
                    className="group flex items-center gap-4 py-4 px-2 border-b border-cream-300 cursor-pointer"
                    data-testid={`module-card-${module.id}`}
                  >
                    <span className="flex items-center gap-2 w-20 flex-shrink-0">
                      <Check className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#8F650F" }} strokeWidth={2.5} />
                      <span className="font-display text-lg text-sand-400 leading-none">Nº {nr}</span>
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg text-sand-500 truncate leading-snug transition-transform duration-300 group-hover:translate-x-1">
                        {cleanTitle}
                      </h3>
                      <div className="font-mono text-[11px] uppercase font-semibold mt-0.5" style={{ letterSpacing: "0.18em", color: "#8F650F" }}>
                        Gelesen{quizScore !== null && ' · Quiz bestanden'}
                      </div>
                    </div>
                    <span className="font-mono text-[11px] text-sand-500 flex-shrink-0 hidden sm:block">{metaTime}</span>
                  </div>
                );
              }

              // STATE: Current — Lesezeichen-Band
              if (isCurrent) {
                const subLessons = moduleProgress?.lessons_done && moduleProgress?.lessons_total
                  ? ` · ${moduleProgress.lessons_done} von ${moduleProgress.lessons_total} Lektionen`
                  : '';
                return (
                  <div
                    key={module.id}
                    onClick={() => navigate(`/module/${module.id}`)}
                    className="group relative flex items-center gap-4 py-4 pl-4 pr-2 border-b border-cream-300 cursor-pointer"
                    style={{ backgroundColor: "#EFE8DC" }}
                    data-testid={`module-card-${module.id}`}
                  >
                    <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-terra" />
                    <span className="w-20 flex-shrink-0 font-display text-lg text-terra leading-none">Nº {nr}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg text-espresso truncate leading-snug transition-transform duration-300 group-hover:translate-x-1">
                        {cleanTitle}
                      </h3>
                      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra font-semibold mt-0.5">
                        Hier weiterlesen{subLessons}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/module/${module.id}`); }}
                      className="font-display italic text-terra hover:text-terra-dark text-[15px] inline-flex items-center gap-1 flex-shrink-0 transition-colors"
                      data-testid={`module-cta-${module.id}`}
                    >
                      Weiterlesen <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <span className="font-mono text-[11px] text-sand-600 flex-shrink-0 hidden sm:block">{metaTime}</span>
                  </div>
                );
              }

              // STATE: Locked / future (Admins duerfen zur Pruefung direkt rein)
              if (user?.role === 'admin') {
                return (
                  <div
                    key={module.id}
                    onClick={() => navigate(`/module/${module.id}`)}
                    className="group flex items-center gap-4 py-4 px-2 border-b border-cream-300 cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
                    data-testid={`module-card-${module.id}`}
                  >
                    <span className="flex items-center gap-2 w-20 flex-shrink-0">
                      <span className="font-display text-lg text-sand-400 leading-none">Nº {nr}</span>
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg text-sand-600 truncate leading-snug transition-transform duration-300 group-hover:translate-x-1">{cleanTitle}</h3>
                      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra font-semibold mt-0.5">Admin-Vorschau · für Teilnehmer gesperrt bis Vorgänger gelesen</div>
                    </div>
                    <span className="font-mono text-[11px] text-sand-500 flex-shrink-0 hidden sm:block">{metaTime}</span>
                  </div>
                );
              }
              return (
                <div
                  key={module.id}
                  onClick={() => openGapsModal(module)}
                  className="group flex items-center gap-4 py-4 px-2 border-b border-cream-300 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                  data-testid={`module-card-${module.id}`}
                >
                  <span className="flex items-center gap-2 w-20 flex-shrink-0">
                    <Lock className="h-3.5 w-3.5 text-sand-400 flex-shrink-0" strokeWidth={1.8} />
                    <span className="font-display text-lg text-sand-400 leading-none">Nº {nr}</span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg text-sand-600 truncate leading-snug transition-transform duration-300 group-hover:translate-x-1">
                      {cleanTitle}
                    </h3>
                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-sand-500 mt-0.5">
                      Noch verschlossen · Klick zeigt, was fehlt
                    </div>
                  </div>
                  <span className="font-mono text-[11px] text-sand-500 flex-shrink-0 hidden sm:block">{metaTime}</span>
                </div>
              );
            });
          })()}
          </div>
        </div>
        )}

        {/* Related courses footer — Cross-sell */}
        {!isBG && (() => {
          // Hardcoded related-course suggestions for now (later: API-based by category)
          const currentSlug = course?.id || courseId || '';
          const allRelated = [
            { slug: 'wundmanagement-aufbau', title: 'Wundmanagement Aufbaumodul', duration: '60 UE · Zertifikat', category: 'Wunden' },
            { slug: 'praxisanleitung', title: 'Weiterbildung Praxisanleitung', duration: '300 UE · §4 PflAPrV', category: 'Leitung' },
            { slug: 'pdl-weiterbildung-teil1', title: 'PDL-Weiterbildung Teil 1', duration: '460 UE · staatl. anerkannt', category: 'Leitung' },
            { slug: 'palliativpflege', title: 'Weiterbildung Palliativpflege', duration: '120 UE · DGP-konform', category: 'Spezialisierung' },
            { slug: 'hygienebeauftragter', title: 'Hygienebeauftragter in der Pflege', duration: '80 UE · 2-jährig', category: 'Pflicht' },
            { slug: 'gerontopsychiatrie', title: 'Gerontopsychiatrische Fachweiterbildung', duration: '480 UE · Aufbau', category: 'Spezialisierung' },
          ];
          // Pick 3 cards that don't match current slug
          const related = allRelated.filter(r => !currentSlug.includes(r.slug)).slice(0, 3);
          if (related.length === 0) return null;

          return (
            <div className="mt-12 pt-8 border-t border-cream-300" data-testid="related-courses-section">
              <div className="flex items-center gap-2.5 mb-1.5">
                <span className="w-6 h-[2px] bg-terra" />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-terra">Weiterlesen · Ausgabe 2026</span>
              </div>
              <h3 className="font-display text-2xl text-espresso tracking-tight leading-tight mb-1.5">
                Das könnte dich <span className="italic text-terra">auch interessieren</span>.
              </h3>
              <p className="text-sand-600 text-sm mb-5">
                Weitere AZAV-zertifizierte Fortbildungen im Dickies-Campus-Shop
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((c, ri) => (
                  <a
                    key={c.slug}
                    href={`https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=related&utm_content=${encodeURIComponent(currentSlug)}&utm_campaign=cross-sell`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-cream-100 border border-cream-300 rounded-2xl p-4 hover:shadow-hover hover:border-terra/30 transition-all group"
                    data-testid={`related-course-${c.slug}`}
                  >
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="font-display text-lg text-terra leading-none">Nº {String(ri + 1).padStart(2, '0')}</span>
                      <span className="text-[10.5px] text-peach-dark uppercase tracking-wider font-semibold">
                        {c.category}
                      </span>
                    </div>
                    <h4 className="font-heading font-semibold text-sand-800 leading-snug mb-2">
                      {c.title}
                    </h4>
                    <p className="text-xs text-sand-500 mb-3">{c.duration}</p>
                    <span className="inline-flex items-center gap-1 text-terra text-sm font-semibold group-hover:gap-2 transition-all">
                      Mehr erfahren <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Pflicht-Fortbildung Frühbucher-Banner */}
        {(() => {
          const totalModules = modules.length;
          const completedCount = progress.filter(p => p.completed || p.quiz_passed).filter(p => !p.fallbeispiele_required || p.fallbeispiele_done).length;
          const isCompleted = totalModules > 0 && completedCount >= totalModules;
          // Heuristic: course title hints at "Pflicht"-Themen (Hygiene, Brandschutz, Datenschutz, Erste Hilfe)
          const title = (course?.title || '').toLowerCase();
          const isPflichtCourse = /hygiene|brandschutz|datenschutz|erste hilfe|jahresfortbildung/.test(title);
          if (!isCompleted || !isPflichtCourse) return null;

          // Year for next mandatory refresher
          const nextYear = new Date().getFullYear() + 1;
          return (
            <div className="mt-6 bg-peach-50 border border-peach-100 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="pflicht-banner">
              <div className="w-12 h-12 rounded-full bg-peach-100 flex items-center justify-center flex-shrink-0">
                <Award className="h-6 w-6 text-peach-dark" strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-sand-800 mb-0.5">
                  Pflichtfortbildung {nextYear} schon planen?
                </h4>
                <p className="text-sand-600 text-sm">
                  Sichere dir jetzt deinen Platz für nächstes Jahr — als Frühbucher mit 15% Rabatt.
                </p>
              </div>
              <a
                href={`https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=fruehbucher&utm_content=${encodeURIComponent(course?.id || '')}&utm_campaign=pflicht-${nextYear}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-terra hover:bg-terra-dark text-white rounded-full px-5 h-10 inline-flex items-center font-semibold whitespace-nowrap transition-colors"
                data-testid="pflicht-banner-cta"
              >
                Platz sichern
              </a>
            </div>
          );
        })()}
      </div>

      {/* "Was fehlt"-Modal — comprehensive gap-check across all previous modules */}
      {gapsModal && (
        <div
          className="fixed inset-0 z-[60] bg-espresso/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setGapsModal(null)}
          data-testid="gaps-modal"
        >
          <div
            className="bg-cream-50 rounded-3xl max-w-2xl w-full max-h-[88vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 overflow-y-auto p-5 sm:p-6">
              <ProgressGapsView
                variant="modal"
                loading={gapsLoading}
                error={Boolean(gapsModal.error)}
                gaps={gapsModal.gaps || []}
                lockedModuleOrder={gapsModal.lockedModuleOrder}
                lockedModuleTitle={gapsModal.lockedModuleTitle}
                onClose={() => setGapsModal(null)}
                onNavigateToModule={(moduleId, target) => {
                  setGapsModal(null);
                  navigate(`/module/${moduleId}#${target}`);
                }}
              />
            </div>
            <div className="px-5 sm:px-6 py-3 bg-cream-100 border-t border-cream-300 flex items-center justify-end flex-shrink-0">
              <button
                onClick={() => setGapsModal(null)}
                className="px-5 py-2 rounded-full bg-sand-800 text-cream-50 text-sm font-medium hover:bg-espresso transition-colors"
                data-testid="gaps-modal-close"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course-script PDF download removed — see "Lernpaket kaufen" button. */}
    </Layout>
  );
};


export default CourseView;
